import TextInput from "components/common/custom/forms/BootTextField/TextField";
import FalconComponentCard from "components/common/FalconComponentCard";
import MultiSelect from "components/common/MultiSelect";
import React, { useEffect } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import SnomedCodeSelect from "./SnomedCodeSelect";
import BootDatePicker from "components/common/custom/forms/BootDatePicker";
import DiagnosisCodeSelect from "./DiagnosisCode Select";
import { useSearchParams } from "react-router-dom";
import {
  GetAllBodySite,
  GetMissingTooth,
  GetServiceCodeDescription,
} from "../config/preauthorizationApi";

const MedicalDetails = ({ watch, setValue, control }) => {
  const dispatch = useDispatch();
  const {
    stepFormData,
    missingTeethList,
    bodySubSite,
    missingTeethListReason,
    viewPreauthorizationInfo,
  } = useSelector((state) => state.preauthorization);
  const { settingData } = useSelector((state) => state.settings);

  const [searchParams] = useSearchParams();
  const reuse = searchParams.get("reuse") === "true";
  const edit = searchParams.get("edit") === "true";
  const followUp = searchParams.get("followup") === "true";

  const preauthRequestMedical =
    viewPreauthorizationInfo?.provider_request?.medical_details;
  const preauthVitals = viewPreauthorizationInfo?.provider_request?.vitals;

  const aspectofConditionCode = preauthRequestMedical?.onset;

  useEffect(() => {
    if (missingTeethList?.length === 0) {
      dispatch(GetMissingTooth());
    }
    if (bodySubSite?.length === 0) {
      dispatch(GetAllBodySite());
    }
  }, [missingTeethList, bodySubSite]);

  useEffect(() => {
    const fetchAspectCondition = async () => {
      if (!reuse && !edit && !followUp) return;
      setValue(
        "chief_complaint",
        watch("chief_complaint") || preauthRequestMedical?.chiefcomplaint
      );
      setValue("icu_hours", watch("icu_hours") || preauthVitals?.icuhours);
      setValue(
        "ventilation_hours",
        watch("ventilation_hours") || preauthVitals?.ventilationhours
      );
      setValue(
        "treatment_plan",
        watch("treatment_plan") || preauthRequestMedical?.treatmentplan
      );
      setValue(
        "patient_history",
        watch("patient_history") || preauthRequestMedical?.patienthistory
      );
      setValue(
        "physical_examination",
        watch("physical_examination") ||
          preauthRequestMedical?.physicalexamination
      );
      setValue(
        "history_present_illness",
        watch("history_present_illness") ||
          preauthRequestMedical?.historypresentillness
      );
      setValue(
        "significant_sign",
        watch("significant_sign") || preauthRequestMedical?.significantsigns
      );
      setValue(
        "clinical_data",
        watch("clinical_data") || preauthRequestMedical?.clinicaldata
      );
      setValue(
        "other_conditions",
        watch("other_conditions") || preauthRequestMedical?.otherconditions
      );
      setValue(
        "aspect_date",
        watch("aspect_date") || preauthRequestMedical?.onsetdate
      );
      setValue(
        "missing_tooth",
        watch("missing_tooth") || preauthRequestMedical?.missingtooth
      );
      setValue(
        "missing_tooth_number",
        watch("missing_tooth_number") ||
          missingTeethList?.find(
            (item) =>
              item?.oral_code === preauthRequestMedical?.missingtooth?.tooth_no
          )
      );
      setValue(
        "missing_tooth_date",
        watch("missing_tooth_date") || preauthRequestMedical?.missingtooth?.date
      );
      setValue(
        "missing_reason",
        watch("missing_reason") ||
          missingTeethListReason?.find(
            (res) =>
              res?.msngtoothcde_code ==
              preauthRequestMedical?.missingtooth?.name
          )
      );
      try {
        if (aspectofConditionCode) {
          const response = await dispatch(
            GetServiceCodeDescription({
              diag_code: aspectofConditionCode,
            })
          ).unwrap();

          const data = response?.data?.Diagnosis_code?.[0] || {};

          setValue("aspect_of_condition", data);
        }
      } catch (error) {
        console.error("Failed to fetch aspect of condition:", error);
      }
    };

    fetchAspectCondition();
  }, [reuse, edit, followUp, viewPreauthorizationInfo, aspectofConditionCode]);

  useEffect(() => {
    if (settingData?.length > 0) {
      const data = settingData?.[0];

      setValue(
        "treatment_plan",
        watch("treatment_plan") || data?.fieldsetting_txt?.sup_info_trtpln
      );
      setValue(
        "patient_history",
        watch("patient_history") ||
          data?.fieldsetting_txt?.sup_info_pathistry ||
          ""
      );
      setValue(
        "physical_examination",
        watch("physical_examination") ||
          data?.fieldsetting_txt?.sup_info_phyexmn
      );
      setValue(
        "history_present_illness",
        watch("history_present_illness") ||
          data?.fieldsetting_txt?.sup_info_histrypreill
      );
    } else if (stepFormData) {
      setValue("chief_complaint", stepFormData?.chief_complaint);
      setValue("significant_sign", stepFormData?.significant_sign);
      setValue("clinical_data", stepFormData?.clinical_data);
      setValue("other_conditions", stepFormData?.other_conditions);
      setValue("aspect_of_condition", stepFormData?.aspect_of_condition);
      setValue("aspect_start_date", stepFormData?.aspect_start_date);
      setValue("aspect_end_date", stepFormData?.aspect_end_date);
      setValue("treatment_plan", stepFormData?.treatment_plan);
      setValue("patient_history", stepFormData?.patient_history);
      setValue("missing_tooth", stepFormData?.missing_tooth);
      setValue("missing_tooth_number", stepFormData?.missing_tooth_number);
      setValue("missing_tooth_date", stepFormData?.missing_tooth_date);
      setValue("missing_reason", stepFormData?.missing_reason);
      setValue("physical_examination", stepFormData?.physical_examination);
      setValue(
        "history_present_illness",
        stepFormData?.history_present_illness
      );
      setValue("icu_hours", stepFormData?.icu_hours);
      setValue("ventilation_hours", stepFormData?.ventilation_hours);
      setValue("snomed_code", stepFormData?.snomed_code);
    }
  }, [
    stepFormData,
    followUp,
    reuse,
    edit,
    searchParams,
    settingData,
    missingTeethList,
  ]);

  const isInstitutional =
    stepFormData?.preauthorization_type?.claim_types_name === "Institutional";
  return (
    <FalconComponentCard className="custom-card">
      <FalconComponentCard.Body className="cutom-body">
        <Row>
          <Col xs={12}>
            <Row className="align-items-center">
              <Col xs={12} xl={3}>
                <h5 className="fs-10">
                  Chief Complaint <span className="text-danger">*</span>
                </h5>
              </Col>
              <Col xs={12} xl={9}>
                <TextInput
                  name="chief_complaint"
                  control={control}
                  required={true}
                  rules={{ required: true }}
                  size="sm"
                  multiline={true}
                  rows={1}
                  placeholder="Chief Complaint"
                />
              </Col>
            </Row>
          </Col>

          <Col xs={12} className="mt-3">
            <Row className="align-items-center">
              <Col xs={12} xl={3}>
                <h5 className="fs-10">
                  Treatment Plan <span className="text-danger">*</span>
                </h5>
              </Col>
              <Col xs={12} xl={9}>
                <TextInput
                  name="treatment_plan"
                  control={control}
                  required={true}
                  rules={{ required: true }}
                  size="sm"
                  multiline={true}
                  rows={1}
                  placeholder="Treatment Plan"
                />
              </Col>
            </Row>
          </Col>
          <Col xs={12} className="mt-3">
            <Row className="align-items-center">
              <Col xs={12} xl={3}>
                <h5 className="fs-10">
                  Patient History <span className="text-danger">*</span>
                </h5>
              </Col>
              <Col xs={12} xl={9}>
                <TextInput
                  name="patient_history"
                  control={control}
                  required={true}
                  rules={{ required: true }}
                  size="sm"
                  multiline={true}
                  rows={1}
                  placeholder="Patient History"
                />
              </Col>
            </Row>
          </Col>
          <Col xs={12} className="mt-3">
            <Row className="align-items-center">
              <Col xs={12} xl={3}>
                <h5 className="fs-10">
                  Physical Examination <span className="text-danger">*</span>
                </h5>
              </Col>
              <Col xs={12} xl={9}>
                <TextInput
                  name="physical_examination"
                  control={control}
                  required={true}
                  rules={{ required: true }}
                  size="sm"
                  multiline={true}
                  rows={1}
                  placeholder="Physical Examination"
                />
              </Col>
            </Row>
          </Col>
          <Col xs={12} className="mt-3">
            <Row className="align-items-center">
              <Col xs={12} xl={3}>
                <h5 className="fs-10">
                  History Of Present Illness{" "}
                  <span className="text-danger">*</span>
                </h5>
              </Col>
              <Col xs={12} xl={9}>
                <TextInput
                  name="history_present_illness"
                  control={control}
                  required={true}
                  rules={{ required: true }}
                  size="sm"
                  multiline={true}
                  rows={1}
                  placeholder="History Of Present Illness"
                />
              </Col>
            </Row>
          </Col>

          <Col xs={12} className="mt-3">
            <Row className="align-items-center">
              <Col xs={12} xl={3}>
                <h5 className="fs-10">Significant Sign</h5>
              </Col>
              <Col xs={12} xl={9}>
                <TextInput
                  name="significant_sign"
                  control={control}
                  required={true}
                  rules={{ required: false }}
                  size="sm"
                  multiline={true}
                  rows={1}
                  placeholder="Significant Sign"
                />
              </Col>
            </Row>
          </Col>
          <Col xs={12} className="mt-3">
            <Row className="align-items-center">
              <Col xs={12} xl={3}>
                <h5 className="fs-10">Clinical Data</h5>
              </Col>
              <Col xs={12} xl={9}>
                <TextInput
                  name="clinical_data"
                  control={control}
                  required={true}
                  rules={{ required: false }}
                  size="sm"
                  multiline={true}
                  rows={1}
                  placeholder="Clinical Data"
                />
              </Col>
            </Row>
          </Col>
          <Col xs={12} className="mt-3">
            <Row className="align-items-center">
              <Col xs={12} xl={3}>
                <h5 className="fs-10">Other Conditions</h5>
              </Col>
              <Col xs={12} xl={9}>
                <TextInput
                  name="other_conditions"
                  control={control}
                  required={true}
                  rules={{ required: false }}
                  size="sm"
                  multiline={true}
                  rows={1}
                  placeholder="Other Conditions"
                />
              </Col>
            </Row>
          </Col>
          {/* <Col xs={12} className="mt-3">
            <Row className="align-items-center">
              <Col xs={12} xl={3}>
                <h5 className="fs-10">Snomed Codes</h5>
              </Col>
              <Col xs={12} xl={9}>
                <SnomedCodeSelect control={control} setValue={setValue} />
              </Col>
            </Row>
          </Col> */}
          <Col xs={12}>
            <Row className="align-items-center">
              <Col xs={12} xl={3}>
                <h5 className="fs-10">Aspect of condition</h5>
              </Col>
              <Col xs={12} xl={7} className="mt-3">
                <DiagnosisCodeSelect
                  watch={watch}
                  name={"aspect_of_condition"}
                  control={control}
                  setValue={setValue}
                />
              </Col>
              <Col xs={12} xl={2} className="mt-3">
                <BootDatePicker
                  name="aspect_date"
                  label=""
                  control={control}
                  rules={{ required: watch("aspect_of_condition") }}
                  placeholder="Start Date"
                  selected={watch("aspect_date")}
                />
              </Col>
            </Row>
          </Col>
          {isInstitutional && (
            <Col xs={12} className="mt-3">
              <Row className="align-items-center">
                <Col xs={12} xl={6}>
                  <Row>
                    <Col xs={12} md={6}>
                      <h5 className="fs-10">ICU Hours</h5>
                    </Col>
                    <Col xs={12} xl={6}>
                      <TextInput
                        name="icu_hours"
                        control={control}
                        required={true}
                        rules={{ required: false }}
                        type="number"
                        placeholder="ICU Hours"
                        min={0}
                        size="sm"
                      />
                    </Col>
                  </Row>
                </Col>
                <Col xs={12} xl={6}>
                  <Row className="align-items-center">
                    <Col xs={12} md={4}>
                      <h5 className="fs-10">Ventilation Hours</h5>
                    </Col>
                    <Col xs={12} xl={8}>
                      <TextInput
                        name="ventilation_hours"
                        control={control}
                        required={true}
                        rules={{ required: false }}
                        type="number"
                        placeholder="Ventilation Hours"
                        min={0}
                        size="sm"
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          )}

          <Col xs={12}>
            <Row className="align-items-center">
              <Col xs={12} sm={12} xl={6}>
                <Row className="align-items-center">
                  <Col xs={6} md={7}>
                    <h5 className="fs-10 mb-0 mt-2">Missing Tooth?</h5>
                  </Col>
                  <Col xs={6} md={5}>
                    <Controller
                      name="missing_tooth"
                      control={control}
                      defaultValue={false}
                      render={({ field }) => (
                        <Form.Switch
                          {...field}
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          label=""
                          disabled={false}
                          size="lg"
                        />
                      )}
                    />
                  </Col>
                </Row>
              </Col>
              {watch("missing_tooth") && (
                <Col xs={12} sm={6} xl={2} className="mt-2">
                  <Controller
                    name="missing_tooth_number"
                    control={control}
                    rules={{ required: watch("missing_tooth") }}
                    render={({ field }) => (
                      <MultiSelect
                        {...field}
                        isMulti={false}
                        isClearable
                        menuPlacement="auto"
                        closeMenuOnSelect={false}
                        placeholder="Select"
                        value={
                          field.value
                            ? {
                                value: field.value.oral_code,
                                label: field.value.oralc_description,
                              }
                            : null
                        }
                        onChange={(selected) =>
                          setValue(
                            "missing_tooth_number",
                            selected
                              ? {
                                  oral_code: selected.value,
                                  oralc_description: selected.label,
                                }
                              : null
                          )
                        }
                        formatOptionLabel={({ label }) => <div>{label}</div>}
                        getOptionValue={(option) => option.value}
                        options={missingTeethList?.map((item) => ({
                          value: item?.oral_code,
                          label: item?.oralc_description,
                        }))}
                      />
                    )}
                  />
                </Col>
              )}
              {watch("missing_tooth") && (
                <Col xs={12} sm={6} xl={2} className="mt-2">
                  <BootDatePicker
                    name="missing_tooth_date"
                    label=""
                    control={control}
                    rules={{ required: watch("missing_tooth") }}
                    // isRequired={watch("missing_tooth")}
                    placeholder="Missing Date"
                    selected={watch("missing_tooth_date")}
                  />
                </Col>
              )}
              {watch("missing_tooth") && (
                <Col xs={12} sm={6} xl={2} className="mt-2">
                  <Controller
                    name="missing_reason"
                    control={control}
                    rules={{ required: watch("missing_tooth") }}
                    render={({ field }) => (
                      <MultiSelect
                        {...field}
                        menuPlacement="auto"
                        closeMenuOnSelect={false}
                        isMulti={false}
                        isClearable={true}
                        options={missingTeethListReason?.map((item) => ({
                          value: item.msngtoothcde_code,
                          label: item.msngtoothcde_name,
                        }))}
                        value={
                          field.value
                            ? {
                                value: field.value.msngtoothcde_code,
                                label: field.value.msngtoothcde_name,
                              }
                            : null
                        }
                        onChange={(selected) => {
                          setValue(
                            "missing_reason",
                            selected
                              ? {
                                  msngtoothcde_code: selected.value,
                                  msngtoothcde_name: selected.label,
                                }
                              : null
                          );
                        }}
                        formatOptionLabel={({ label }) => <div>{label}</div>}
                        getOptionValue={(option) => option.value}
                        placeholder="Choose ..."
                      />
                    )}
                  />
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      </FalconComponentCard.Body>
    </FalconComponentCard>
  );
};

export default MedicalDetails;
